import { Coordinates } from "src/api/generated/common/address/coordinates.ts";
import { loadGoogleMapsLibrary } from "src/components/common/googleMaps/googleMapsApi.ts";
import { toGoogleLatLng } from "src/components/common/googleMaps/googleMapsUtils.ts";

export interface AavoAdvancedMarker {
	googleMarker: google.maps.marker.AdvancedMarkerElement;
	getPosition: () => google.maps.LatLng | undefined;
	remove: () => void;
	setPosition: (position: google.maps.LatLngLiteral) => void;
}

export interface CircleAdvancedMarkerOptions {
	map: google.maps.Map;
	coords: Coordinates;
	color: string;
	gmpDraggable?: boolean;
	title?: string;
}

export async function createCircleAdvancedMarker({
	map,
	coords,
	color,
	gmpDraggable,
	title,
}: CircleAdvancedMarkerOptions): Promise<AavoAdvancedMarker> {
	const { AdvancedMarkerElement } = await loadGoogleMapsLibrary("marker");

	const marker = new AdvancedMarkerElement({
		map: map,
		position: toGoogleLatLng(coords),
		gmpDraggable: gmpDraggable,
		gmpClickable: true,
		title: title,
	});
	marker.append(createCircleMarkerContent(color));

	return createAavoAdvancedMarker(marker);
}

export async function createAdvancedMarker({
	map,
	position,
	gmpDraggable,
	title,
}: {
	map: google.maps.Map;
	position: google.maps.LatLngLiteral;
	gmpDraggable?: boolean;
	title?: string;
}): Promise<AavoAdvancedMarker> {
	const { AdvancedMarkerElement } = await loadGoogleMapsLibrary("marker");

	const marker = new AdvancedMarkerElement({
		map: map,
		position: position,
		gmpDraggable: gmpDraggable,
		gmpClickable: true,
		title: title,
	});

	return createAavoAdvancedMarker(marker);
}

function createAavoAdvancedMarker(marker: google.maps.marker.AdvancedMarkerElement): AavoAdvancedMarker {
	return {
		googleMarker: marker,
		getPosition: () => getMarkerPosition(marker),
		remove: () => {
			marker.map = null;
		},
		setPosition: (position) => {
			marker.position = position;
		},
	};
}

function getMarkerPosition(marker: google.maps.marker.AdvancedMarkerElement): google.maps.LatLng | undefined {
	const position = marker.position;
	if (position == null) return undefined;

	if (position instanceof google.maps.LatLng) return position;

	return new google.maps.LatLng({
		lat: position.lat,
		lng: position.lng,
	});
}

function createCircleMarkerContent(color: string): HTMLElement {
	const markerContent = document.createElement("div");
	markerContent.style.width = "16px";
	markerContent.style.height = "16px";
	markerContent.style.borderRadius = "50%";
	markerContent.style.backgroundColor = color;
	markerContent.style.border = "2px solid #ffffff";
	markerContent.style.boxSizing = "border-box";
	return markerContent;
}
